import { useMutation } from "@tanstack/react-query";
import { UserInfo } from "@/types/auth";
import { api } from "@/utils/axiosInterceptor";
import { getAccessToken } from "@/store/authStore";

const API_BASE_URL = import.meta.env.VITE_SUBMIT_API_BASE_URL;
const X_API_KEY = import.meta.env.VITE_X_API_KEY;

/** Result of reconciling one invoice's status against LHDN / MyInvois. */
export type InvoiceStatusSyncResult = {
  invoiceId: string;
  uuid: string | null;
  previousStatus: string | null;
  newStatus: string | null;
  changed: boolean;
  synced: boolean;
  outcome: "updated" | "unchanged" | "skipped" | "failed";
  message: string;
};

/** Result of a cancellation attempt */
export type CancellationResult = {
  outcome: "cancelled" | "already-cancelled" | "not-cancellable" | "failed";
  success: boolean;
  message: string;
  previousStatus: string | null;
  currentStatus: string | null;
  synchronized: boolean;
  uuid: string | null;
  consolidatedRevert?: { reverted: boolean; message: string };
};

export const useEInvoices = () => {
  const submitEInvoice = useMutation({
    mutationFn: async ({ invoice, user }: { invoice: any; user: UserInfo }) => {
      try {
        const accessToken = getAccessToken();
        const response = await fetch(`${API_BASE_URL}/submit/${user.tin}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": X_API_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(invoice),
        });

        // The body is not always JSON
        const rawText = await response.text();
        let responseBody: any = null;
        try {
          responseBody = rawText ? JSON.parse(rawText) : null;
        } catch {
          responseBody = { message: rawText };
        }

        if (!response.ok) {
          // Attach the HTTP status
          const err: any = responseBody ?? {};
          err.__httpStatus = response.status;
          err.__reachedLhdn = false;
          throw err;
        }

        return responseBody;
      } catch (error) {
        console.error("Fetch error:", error);
        throw error;
      }
    },
    onError: (error) => {
      console.error("Error submitting invoice:", error);
    },
  });

  const getSubmission = async ({
    uuid,
    user,
  }: {
    uuid: string;
    user: UserInfo;
  }) => {
    const response = await fetch(
      `https://e-invoice-submit.onrender.com/api/v1/validate/${user.tin}/document/${uuid}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "5f31ae89-5569-4ffc-be1b-47195b599b90",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
  };

  const validateEInvoice = async (uuid: string, onBehalfOf: string) => {
    try {
      await api.post("/validate-einvoice", { uuid, onBehalfOf });
    } catch (error) {
      console.error(`Error validating details for UUID ${uuid}:`, error);
    }
  };

  const reprocessStuckSubmission = async (
    invoiceId: string,
    onBehalfOf: string,
  ) => {
    try {
      await api.post("/einvoice/reprocess-submission", {
        invoiceId,
        onBehalfOf,
      });
    } catch (error) {
      console.error(
        `Error reprocessing stuck submission for invoiceId ${invoiceId}:`,
        error,
      );
    }
  };

  /**
   * Synchronize a single invoice's status with LHDN / MyInvois.
   */
  const syncInvoiceStatus = async (
    invoiceId: string,
  ): Promise<InvoiceStatusSyncResult> => {
    const response = await api.post("/einvoice/status/sync", { invoiceId });
    return response.data;
  };

  /**
   * Cancel an e-invoice through the QuickBill backend.
   */
  const cancelEInvoice = useMutation({
    mutationFn: async ({
      invoiceId,
      reason,
    }: {
      invoiceId: string;
      reason: string;
    }): Promise<CancellationResult> => {
      try {
        const response = await api.post("/einvoice/cancel", {
          invoiceId,
          reason,
        });
        return response.data;
      } catch (error: any) {
        const data = error?.response?.data;

        // The backend answers 409/502
        if (data && typeof data === "object" && "outcome" in data) {
          return data as CancellationResult;
        }

        console.error("cancelEInvoice error:", error);
        return {
          outcome: "failed",
          success: false,
          message:
            data?.message ??
            "Failed to cancel invoice. Please check your connection and try again.",
          previousStatus: null,
          currentStatus: null,
          synchronized: false,
          uuid: null,
        };
      }
    },
  });

  const rejectEInvoice = useMutation({
    mutationFn: async ({
      uuid,
      user,
      reason,
    }: {
      uuid: string;
      user: UserInfo;
      reason: string;
    }) => {
      try {
        const accessToken = getAccessToken();
        const url = `${API_BASE_URL}/reject/${user.tin}/${uuid}`;

        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": X_API_KEY,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ reason }),
        });

        if (!response.ok) {
          const errorMessage = await response.text();
          throw new Error(`Failed to reject E-Invoice: ${errorMessage}`);
        }

        return await response.json();
      } catch (error) {
        console.error("rejectEInvoice error:", error);
        throw error; // Rethrow so caller can catch it
      }
    },
    onSuccess: async (_, variables) => {
      const { uuid, user } = variables;
      await validateEInvoice(uuid, user.tin);
    },
  });

  const processEInvoice = useMutation({
    mutationFn: (data: {
      invoiceId: string;
      onBehalfOf: string;
      lhdnSubmissionResponse: any;
    }) => api.post(`/process-einvoice`, data),
  });

  const insertTemporarySubmissionStatus = useMutation({
    mutationFn: (data: {
      internalId: string;
      invoiceId: string;
      onBehalfOf: string;
      uuid?: string | null;
      submissionUid?: string | null;
      lhdnSubmissionResponse?: any;
    }) => api.post("/einvoice/submission/temp", data),
  });

  const getTaxpayerTin = async ({
    idType,
    idValue,
    user,
  }: {
    idType: string;
    idValue: string;
    user: UserInfo;
  }) => {
    const accessToken = getAccessToken();
    const response = await fetch(
      `${API_BASE_URL}/search-tin/${user.tin}?idType=${idType}&idValue=${idValue}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": X_API_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Unknown error");
    }

    return data;
  };

  // ============================================================
  // Submission Safeguard Functions
  // ============================================================

  /**
   * Check which invoices are locked (already submitted/submitting).
   * Call before showing submit confirmation.
   */
  const checkSubmissionLocks = async (
    invoiceIds: string[],
  ): Promise<
    Record<string, { locked: boolean; reason?: string; status?: string }>
  > => {
    const response = await api.post("/einvoice/submission/lock-check", {
      invoiceIds,
    });
    return response.data;
  };

  /**
   * Acquire a server-side submission lock for a single invoice
   */
  const acquireSubmissionLock = async (
    invoiceId: string,
  ): Promise<{ locked: boolean; reason?: string; existingStatus?: string }> => {
    const response = await api.post("/einvoice/submission/acquire-lock", {
      invoiceId,
    });
    return response.data;
  };

  /**
   * Release a "Submitting" lock when the submission failed before reaching LHDN
   */
  const releaseSubmissionLock = async (invoiceId: string) => {
    try {
      await api.post("/einvoice/submission/release-lock", { invoiceId });
    } catch (error) {
      console.error(`Error releasing submission lock for ${invoiceId}:`, error);
    }
  };

  /**
   * Mark an invoice as PendingCheck after a timeout.
   */
  const markPendingCheck = async (invoiceId: string, onBehalfOf: string) => {
    try {
      await api.post("/einvoice/submission/pending-check", {
        invoiceId,
        onBehalfOf,
      });
    } catch (error) {
      console.error(`Error marking pending check for ${invoiceId}:`, error);
    }
  };

  /**
   * Resolve a PendingCheck invoice by querying LHDN.
   */
  const resolvePendingCheck = async (
    invoiceId: string,
    onBehalfOf: string,
  ): Promise<{
    resolved: boolean;
    status: string;
    canResubmit: boolean;
    uuid?: string;
  }> => {
    const response = await api.post("/einvoice/submission/resolve-pending", {
      invoiceId,
      onBehalfOf,
    });
    return response.data;
  };

  return {
    submitEInvoice: submitEInvoice.mutateAsync,
    isSubmitting: submitEInvoice.isPending,
    getSubmission: getSubmission,
    cancelEInvoice: cancelEInvoice.mutateAsync,
    isCancelling: cancelEInvoice.isPending,
    syncInvoiceStatus,
    rejectEInvoice: rejectEInvoice.mutateAsync,
    validateEInvoice,
    reprocessStuckSubmission,
    processEInvoice: processEInvoice.mutateAsync,
    insertTemporarySubmissionStatus:
      insertTemporarySubmissionStatus.mutateAsync,
    getTaxpayerTin,
    // Submission safeguards
    checkSubmissionLocks,
    acquireSubmissionLock,
    releaseSubmissionLock,
    markPendingCheck,
    resolvePendingCheck,
  };
};
